import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Route, Router } from "@angular/router";
import { CommercePlatform } from "@spscommerce/services";
import { template } from "@spscommerce/utils";
import isEmpty from "lodash.isempty";
import startsWith from "lodash.startswith";
import { filter, map } from "rxjs/operators";
import { AttributePresenceInput } from "@spscommerce/ng-utils";

@Component({
    selector: "sps-product-bar",
    templateUrl: "./sps-product-bar.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpsProductBarComponent {
    static readonly displayName = "sps-product-bar";
    static readonly props = {
        productName: { type: "string", required: true },
        productLogoSrc: "string",
        fullWidth: "boolean",
    };

    private commercePlatform = new CommercePlatform();

    @Input() productName: string;
    @Input() productLogoSrc: string;
    @Input() @AttributePresenceInput() fullWidth: boolean;

    @HostBinding("class.sps-navbar-container")
    @HostBinding("class.z-stratum-bar")
    readonly _staticClassBindings = true;

    routes: Route[];
    currentRoute: Route;

    constructor(
        private activatedRoute: ActivatedRoute,
        private changeDetector: ChangeDetectorRef,
        private router: Router
    ) {
        // Named routes in the root app routing module will be used for the navbar.
        this.routes = router.config.filter(
            (route: Route) => route && route.data && !isEmpty(route.data.name)
        );

        // See https://github.com/angular/angular/issues/19420#issuecomment-333071573
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                map(() => {
                    let route = this.activatedRoute.firstChild;
                    let child = route;

                    while (child) {
                        if (child.firstChild) {
                            child = child.firstChild;
                            route = child;
                        } else {
                            child = null;
                        }
                    }

                    return route;
                })
            )
            .subscribe(this.onNavigationEnd.bind(this));
    }

    onNavigationEnd(activeRoute: ActivatedRoute): void {
        /* activeRoute/snapshot may refer to either A) one of the root-level routes in
         * this.routes that comprise the navbar tabs or B) a child route */
        const snapshot = activeRoute.snapshot;

        /* Set which root-level route is current so the CSS style indicating which tab
         * you're on is set appropriately */
        this.currentRoute = this.routes.find((route: Route) =>
            startsWith(this.router.routerState.snapshot.url, route.path, 1)
        );

        // Update state so query params show in the browser's location bar
        this.commercePlatform.updateState();

        // Update page title
        let newTitle = snapshot.data.title
            || (
                this.currentRoute
                ? (this.currentRoute.data.title || this.currentRoute.data.name)
                : ""
            );
        newTitle = template(newTitle)({
            ...snapshot.params,
            ...snapshot.queryParams
        });
        this.commercePlatform.setPageTitle(newTitle);

        this.changeDetector.markForCheck();
    }
}
